import type { Dispatch, SetStateAction } from 'react';
import { TagDescription, Keyword, School, CtfVideo, SchoolUserRole } from '@novaheal/types';
import { SchoolVoucher } from 'hooks/api/backend/managament/getB2BUsers';
import { ClassParticipantType } from 'hooks/api/backend/managament/getClasses';
import {
  EducationProgressionPromptDto,
  EducationProgressionAdjustmentDto,
} from 'hooks/api/backend/user/education-progression';
import { AiSubscriptionProduct, PendingChange } from './AiSubscription';

export enum ModalVariant {
  LOGOUT = 'logout',
  DISLIKE = 'dislike',
  TAG = 'tag',
  KEYWORD = 'keyword',
  IMAGE = 'image',
  VIDEO = 'video',
  FTU_VIDEO = 'ftuVideo',
  STOP_EDITING = 'stopEditing',
  SUBSCRIBE = 'subscribe',
  PAYWALL = 'paywall',
  VOUCHER = 'voucher',
  SHARE = 'share',
  MAIL_VERIFICATION = 'mailVerification',
  CONFIRMATION = 'confirmation',
  ANNOUNCEMENT = 'announcement',
  ADMIN_INVITE = 'adminInvite',
  WORKSHEET_VIDEO = 'worksheetVideo',
  WORKSHEET_ARTICLE = 'worksheetArticle',
  WORKSHEET_DISTRIBUTION = 'worksheetDistribution',
  WORKSHEET_DISTRIBUTION_SAVE = 'worksheetDistributionSave',
  WORKSHEET_DELETE_BLOCK = 'worksheetDeleteBlock',
  WORKSHEET_DELETE = 'worksheetDelete',
  WORKSHEET_SHARE = 'worksheetShare',
  WORKSHEET_TEMPLATE_COPY = 'worksheetTemplateCopy',
  WORKSHEET_TEMPLATE_DELETE = 'worksheetTemplateDelete',
  BETA_WALL = 'betaWall',
  UNSAVED_CHANGES = 'unsavedChanges',
  AI_PRODUCTS = 'aiProducts',
  AI_SUB_CANCELED = 'aiSubCanceled',
  AI_SUB_STILL_ACTIVE = 'aiSubStillActive',
  CASE_ME_NEWSLETTER = 'caseMeNewsletter',
  MAIN_SUB_EXPIRES = 'mainSubExpires',
  CLASS_CREATION = 'classCreation',
  CLASS_PARTICIPANT_CREATION = 'classParticipantCreation',
  CLASS_TEACHER_CREATION = 'classTeacherCreation',
  CLASS_GUIDE_CREATION = 'classGuideCreation',
  CLASS_PARTICIPANT_REMOVAL = 'classParticipantRemoval',
  CLASS_TEACHER_REMOVAL = 'classTeacherRemoval',
  CLASS_GUIDE_REMOVAL = 'classGuideRemoval',
  DELETE_CLASS = 'deleteClass',
  MANAGEMENT_CHANGE_ROLE = 'managementChangeRole',
  USER_WORKSHEETS = 'userWorksheets',
  SEARCH_FILTER = 'searchFilter',
  EDUCATION_PROGRESSION = 'educationProgression',
}
export type ModalProps =
  | ModalLogoutVariantProps
  | ModalDislikeVariantProps
  | ModalTagVariantProps
  | ModalKeywordVariantProps
  | ModalImageVariantProps
  | ModalVideoVariantProps
  | ModalFtuVideoVariantProps
  | ModalStopEditingVariantProps
  | ModalSubscribeVariantProps
  | ModalPaywallVariantProps
  | ModalBetaWallVariantProps
  | ModalShareVariantProps
  | ModalMailVerificationProps
  | ModalVoucherProps
  | ModalConfirmationProps
  | ModalAnnouncementVariantProps
  | ModalAdminInviteVariantProps
  | ModalWorksheetVideoVariantProps
  | ModalWorksheetArticleVariantProps
  | ModalWorksheetDeleteBlockVariantProps
  | ModalWorksheetDistributionVariantProps
  | ModalWorksheetDistributionSaveVariantProps
  | ModalWorksheetDeleteVariantProps
  | ModalWorksheetTemplateCopyVariantProps
  | ModalUnsavedChangesVariantProps
  | ModalWorksheetShareVariantProps
  | ModalAiProductsVariantProps
  | ModalAiSubCancelVariantProps
  | ModalAiSubStillActiveVariantProps
  | ModalCaseMeNewsletterVariantProps
  | ModalMainSubExpiresVariantProps
  | ModalClassCreationVariantProps
  | ModalClassParticipantCreationVariantProps
  | ModalClassTeacherCreationVariantProps
  | ModalClassGuideCreationVariantProps
  | ModalClassParticipantRemovalVariantProps
  | ModalClassTeacherRemovalVariantProps
  | ModalClassGuideRemovalVariantProps
  | ModalDeleteClassProps
  | ModalRoleConfirmationVariantProps
  | ModalUserWorksheetsVariantProps
  | ModalWorksheetTemplateDeleteProps
  | ModalSearchFilterVariantProps
  | ModalEducationProgressionVariantProps;

export type ModalContextValueProps = ModalProps & SharedProps;

export type ModalContextProps = {
  currentModalValues: ModalContextValueProps;
  setCurrentModalValues: (value: ModalContextValueProps) => void;
  defaultModalValues: ModalContextValueProps;
};

export enum ModalTarget {
  APP = 'modal-target-app',
  PAGE_MOBILE = 'modal-target-page--mobile',
  PAGE_DESKTOP = 'modal-target-page--desktop',
}

export type SharedProps = {
  isShown: boolean;
};

// Modal Variants
export type ModalLogoutVariantProps = {
  variant: ModalVariant.LOGOUT;
};

export type ModalTagVariantProps = {
  variant: ModalVariant.TAG;
  title?: string;
  tagDescription?: TagDescription[];
};

export type ModalDislikeVariantProps = {
  variant: ModalVariant.DISLIKE;
  onAccept?: () => void;
  name?: string;
};

export type ModalStopEditingVariantProps = {
  variant: ModalVariant.STOP_EDITING;
  onStopEditing?: () => void;
};

export type ModalKeywordVariantProps = {
  variant: ModalVariant.KEYWORD;
  data?: Keyword;
};

export type ModalImageVariantProps = {
  variant: ModalVariant.IMAGE;
  imgUrl?: string;
  title?: string;
  description?: string;
};

export type ModalVideoVariantProps = {
  variant: ModalVariant.VIDEO;
  videoUrl?: string;
  videoDescription?: string;
  thumbnailUrl?: string;
  isEmbedded?: boolean;
  onVideoEnd?: () => void;
  onVideoAbort?: () => void;
};

export type ModalFtuVideoVariantProps = {
  variant: ModalVariant.FTU_VIDEO;
  video?: CtfVideo;
  onModalClose?: () => void;
};

export type ModalSubscribeVariantProps = {
  variant: ModalVariant.SUBSCRIBE;
};

export type ModalPaywallVariantProps = {
  variant: ModalVariant.PAYWALL;
  target?: ModalTarget;
  onUpgradeClick?: () => void;
};

export type ModalBetaWallVariantProps = {
  variant: ModalVariant.BETA_WALL;
};

export type ModalVoucherProps = {
  variant: ModalVariant.VOUCHER;
  onSuccess?: (schoolData?: School) => void;
};

export type ModalShareVariantProps = {
  variant: ModalVariant.SHARE;
};

export type ModalMailVerificationProps = {
  variant: ModalVariant.MAIL_VERIFICATION;
  target?: ModalTarget;
  shouldGoBack?: boolean;
};

export type ModalConfirmationProps = {
  variant: ModalVariant.CONFIRMATION;
  target?: ModalTarget;
  shouldGoBack?: boolean;
  onConfirm?: () => void;
};

export type ModalWorksheetTemplateDeleteProps = {
  variant: ModalVariant.WORKSHEET_TEMPLATE_DELETE;
  templateName: string;
  onDelete?: () => void;
};
export type ModalAnnouncementVariantProps = {
  variant: ModalVariant.ANNOUNCEMENT;
  title?: string;
  message?: string;
  internalLink?: {
    label: string;
    url: string;
  };
  externalLink?: {
    label: string;
    url: string;
  };
  onClose?: () => void;
};

export type ModalAdminInviteVariantProps = {
  variant: ModalVariant.ADMIN_INVITE;
  selectedVoucherId?: string;
  allVouchers?: SchoolVoucher[];
};

export type ModalWorksheetVideoVariantProps = {
  variant: ModalVariant.WORKSHEET_VIDEO;
  onSelect?: (data: {
    videoUrl: string;
    title: string;
    description?: string;
    thumbnailUrl?: string;
    qrCodeUrl?: string;
  }) => void;
};

export type ModalWorksheetArticleVariantProps = {
  variant: ModalVariant.WORKSHEET_ARTICLE;
  onSelect?: (data: { title: string; description?: string; thumbnailUrl?: string }) => void;
};

export type ModalWorksheetDeleteBlockVariantProps = {
  variant: ModalVariant.WORKSHEET_DELETE_BLOCK;
  onAccept?: () => void;
  onCancel?: () => void;
  name?: string;
};

export type ModalWorksheetDistributionVariantProps = {
  variant: ModalVariant.WORKSHEET_DISTRIBUTION;
  worksheetId?: string;
  worksheetTitle?: string;
};

export type ModalWorksheetDistributionSaveVariantProps = {
  variant: ModalVariant.WORKSHEET_DISTRIBUTION_SAVE;
  onCopyAndSave?: () => Promise<void>;
  onPrimarySave?: () => Promise<void>;
  onCancel?: () => void;
  mode?: 'DISTRIBUTED' | 'DISTRIBUTED_TEMPLATE' | 'TEMPLATE_ONLY';
};

export type ModalWorksheetDeleteVariantProps = {
  variant: ModalVariant.WORKSHEET_DELETE;
  description?: string;
  onDelete?: () => Promise<void>;
};

export type ModalWorksheetShareVariantProps = {
  variant: ModalVariant.WORKSHEET_SHARE;
  worksheetId?: string;
};

export type ModalWorksheetTemplateCopyVariantProps = {
  variant: ModalVariant.WORKSHEET_TEMPLATE_COPY;
  templateId: string;
  initialTitle?: string;
  onCopied?: (worksheetId: string) => void;
};

export type ModalUnsavedChangesVariantProps = {
  variant: ModalVariant.UNSAVED_CHANGES;
  message?: string;
  onSaveAndLeave?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
};

export type ModalAiSubCancelVariantProps = {
  variant: ModalVariant.AI_SUB_CANCELED;
  endDate?: string;
};

export type ModalAiSubStillActiveVariantProps = {
  variant: ModalVariant.AI_SUB_STILL_ACTIVE;
  endDate?: string;
};

export type ModalAiProductsVariantProps = {
  variant: ModalVariant.AI_PRODUCTS;
  products?: AiSubscriptionProduct[];
  pendingChange?: PendingChange;
  isSubExpiring?: boolean;
};

export type ModalCaseMeNewsletterVariantProps = {
  variant: ModalVariant.CASE_ME_NEWSLETTER;
  onClose?: () => void;
  onRedirect?: () => void;
  id?: string;
};

export type ModalMainSubExpiresVariantProps = {
  variant: ModalVariant.MAIN_SUB_EXPIRES;
  endDate?: string;
};

export type ModalClassCreationVariantProps = {
  variant: ModalVariant.CLASS_CREATION;
};

export type ModalClassParticipantCreationVariantProps = {
  variant: ModalVariant.CLASS_PARTICIPANT_CREATION;
  classId?: string;
};

export type ModalClassTeacherCreationVariantProps = {
  variant: ModalVariant.CLASS_TEACHER_CREATION;
  classId?: string;
};

export type ModalClassGuideCreationVariantProps = {
  variant: ModalVariant.CLASS_GUIDE_CREATION;
  classId?: string;
};

export type ModalClassParticipantRemovalVariantProps = {
  variant: ModalVariant.CLASS_PARTICIPANT_REMOVAL;
  participantId?: string;
  participantName?: string;
  participantType?: ClassParticipantType;
  classId?: string;
};

export type ModalClassTeacherRemovalVariantProps = {
  variant: ModalVariant.CLASS_TEACHER_REMOVAL;
  teacherId?: string;
  teacherName?: string;
  classId?: string;
};

export type ModalClassGuideRemovalVariantProps = {
  variant: ModalVariant.CLASS_GUIDE_REMOVAL;
  guideId?: string;
  guideName?: string;
  classId?: string;
};

export type ModalDeleteClassProps = {
  variant: ModalVariant.DELETE_CLASS;
  className?: string;
  onConfirm?: () => void;
};

export type ModalRoleConfirmationVariantProps = {
  variant: ModalVariant.MANAGEMENT_CHANGE_ROLE;
  firstName: string;
  lastName: string;
  newRole: SchoolUserRole;
  currentRole: SchoolUserRole;
  memberOfClasses?: { id: string; name: string }[];
  teacherOfClasses?: { id: string; name: string }[];
  guideOfClasses?: { id: string; name: string }[];
  onConfirm: () => void;
};
export type ModalUserWorksheetsVariantProps = {
  variant: ModalVariant.USER_WORKSHEETS;
  classId?: string;
  studentId?: string;
};

export type ModalSearchFilterVariantProps = {
  variant: ModalVariant.SEARCH_FILTER;
  selectedCeTags: string[];
  selectedKbTags: string[];
  setSelectedCeTags: Dispatch<SetStateAction<string[]>>;
  setSelectedKbTags: Dispatch<SetStateAction<string[]>>;
};

export type ModalEducationProgressionVariantProps = {
  variant: ModalVariant.EDUCATION_PROGRESSION;
  educationProgressionPrompt: EducationProgressionPromptDto;
  onConfirm: () => Promise<void>;
  onAdjust: (data: EducationProgressionAdjustmentDto) => Promise<void>;
  onDismiss: () => Promise<void>;
  isSubmitting?: boolean;
};

// Shared Modal Variant Props
export type SharedModalProps = {
  isShown: boolean;
  handleClose: () => void;
};

// Dynamic options helper for Modal Hook

// get current ModalProps type based on passed variant => This is our variant variable we pass to a function
export type ModalPropsVariant = ModalProps['variant'];

// Helper type to filter out specific Key value pairs. In this case only "variant" because we add isShown to Context type later.
// Testcase: type Test = ExcludeNotOptionFields<{ variant: ModalVariant.DISLIKE; isShown: true }>; // Expected {}
type ExcludeNotOptionFields<O> = { [K in Exclude<keyof O, 'variant'>]: O[K] };

// Extract Modal option props based on variant & ensure args are strictly typed.
// It returns `undefined` when there are no arguments, and the proper type when they exist.
export type ExtractModalOptionsParameters<O, T> = O extends { variant: T }
  ? keyof ExcludeNotOptionFields<O> extends never
    ? undefined
    : ExcludeNotOptionFields<O>
  : never;
